import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { MyAccountState, MyAccountTypes, UsersTypes } from 'store/modules/my_account_store/types';
import { getUsers } from 'store/modules/my_account_store/actions';
import Loader from 'components/atoms/loader';
import ModalSuccessSolicitation from 'components/atoms/modal_success_solicitation';
import UserFormModal, { UserFormMode } from 'components/molecules/user_form_modal';
import UserProfilePanel from 'components/organisms/user_profile_panel';
import { UserActionResponse, UserInfoResponse } from 'services/actions/users_actions';
import {
  Chevron,
  Container,
  PanelBody,
  SubtleBadge,
  ToggleButton,
  UserCard,
  UserName,
} from './styles';

const MAX_ACTIVE_USERS = 5;

const UserCenter: React.FC = () => {
  const dispatch = useDispatch();

  const { my_account, request } = useSelector<ApplicationState, MyAccountState>(
    (state) => state.session.my_account,
  );

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<UserFormMode>('create');
  const [editingUser, setEditingUser] = useState<UsersTypes | null>(null);
  const [editingUserInfo, setEditingUserInfo] = useState<UserInfoResponse | null>(null);
  const [feedback, setFeedback] = useState<{ userId: number; message: string } | null>(null);
  const [globalFeedback, setGlobalFeedback] = useState<string>('');
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    },
    [],
  );
  const [openUserId, setOpenUserId] = useState<number | null>(null);
  const [refreshTick, setRefreshTick] = useState<Record<number, number>>({});

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    if (!globalFeedback) return undefined;
    const timer = setTimeout(() => setGlobalFeedback(''), 8000);
    return () => clearTimeout(timer);
  }, [globalFeedback]);

  const users = my_account?.users || [];
  const loading = request?.users?.loading;
  const initialLoading = loading && users.length === 0;

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) =>
        (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' }),
      ),
    [users],
  );

  const activeUsersCount = useMemo(() => users.filter((u) => u.ativo).length, [users]);
  const limitReached = activeUsersCount >= MAX_ACTIVE_USERS;

  const handleCreate = () => {
    setEditingUser(null);
    setEditingUserInfo(null);
    setModalMode('create');
    setFeedback(null);
    setShowModal(true);
  };

  const handleEdit = (user: UsersTypes, info: UserInfoResponse) => {
    setEditingUser(user);
    setEditingUserInfo(info);
    setModalMode('edit');
    setFeedback(null);
    setShowModal(true);
  };

  const handleSuccess = (response: UserActionResponse) => {
    if (response.action === 'ticket_created') {
      dispatch({ type: MyAccountTypes.MODAL_SUCCESS, modal_success: true });
    } else if (response.action === 'user_created') {
      // O login é criado pelo replicador de autenticação (leva alguns
      // segundos): feedback global + um refresh extra para o novo usuário
      // aparecer na lista. O timer é único e limpo no unmount.
      setGlobalFeedback(response.message);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => dispatch(getUsers()), 5000);
    } else {
      const targetId = response.data?.id ?? editingUser?.id;
      if (targetId) {
        setFeedback({ userId: targetId, message: response.message });
        setOpenUserId(targetId);
        setRefreshTick((prev) => ({ ...prev, [targetId]: (prev[targetId] || 0) + 1 }));
      }
    }
    dispatch(getUsers());
  };

  const handleDeactivated = (userId: number, message: string) => {
    setFeedback({ userId, message });
    setRefreshTick((prev) => ({ ...prev, [userId]: (prev[userId] || 0) + 1 }));
    dispatch(getUsers());
  };

  return (
    <Container className="card border-0 shadow-sm">
      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom">
        <div>
          <h4 className="mb-0 font-weight-bolder">Usuários</h4>
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleCreate}
          disabled={limitReached}
          title={limitReached ? 'Limite de usuários atingido.' : ''}
        >
          <i className="material-icons align-middle mr-2">person_add</i>
          Adicionar usuário
        </button>
      </div>

      <div className="p-4">
        {globalFeedback && (
          <div className="alert alert-success py-2 mb-3" role="alert">
            {globalFeedback}
          </div>
        )}

        {initialLoading ? (
          <Loader />
        ) : (
          <div id="user-center-accordion">
            {users.length === 0 && <p className="text-muted mb-0">Nenhum usuário cadastrado.</p>}

            {sortedUsers.map((user) => {
              const headingId = `user-center-heading-${user.id}`;
              const collapseId = `user-center-collapse-${user.id}`;
              const isOpen = openUserId === user.id;

              return (
                <UserCard key={user.id}>
                  <ToggleButton
                    type="button"
                    id={headingId}
                    aria-expanded={isOpen}
                    aria-controls={collapseId}
                    onClick={() => setOpenUserId(isOpen ? null : user.id)}
                    className="user-center-toggle"
                  >
                    <div className="d-flex align-items-center">
                      <UserName>{user.nome}</UserName>
                      {!user.ativo && <SubtleBadge>Inativo</SubtleBadge>}
                    </div>
                    <Chevron className="material-icons" $isOpen={isOpen}>
                      expand_more
                    </Chevron>
                  </ToggleButton>

                  <PanelBody id={collapseId} aria-labelledby={headingId} $isOpen={isOpen}>
                    {isOpen && (
                      <UserProfilePanel
                        key={`${user.id}-${refreshTick[user.id] || 0}`}
                        user={user}
                        onEdit={(info) => handleEdit(user, info)}
                        onDeactivated={(message) => handleDeactivated(user.id, message)}
                        feedbackMessage={
                          feedback?.userId === user.id ? feedback.message : undefined
                        }
                      />
                    )}
                  </PanelBody>
                </UserCard>
              );
            })}
          </div>
        )}
      </div>

      <UserFormModal
        showModal={showModal}
        setShowModal={setShowModal}
        mode={modalMode}
        user={editingUser}
        initialInfo={editingUserInfo}
        onSuccess={handleSuccess}
      />

      <ModalSuccessSolicitation />
    </Container>
  );
};

export default UserCenter;
